/**
 * Link field.
 *
 * Two rules from the SEO guide are enforced here rather than left to review: anchor text must
 * be descriptive (not "click here"), and internal links point at collctiv.com. Internal link
 * count is one of the few hard targets in the guide — at least two per post.
 */

import type { CustomFieldRender } from '@puckeditor/core';
import type { LinkValue } from '@lp/shared';

/** Mirrors INTERNAL_HOSTS from the Blog Handover Tool. */
const INTERNAL_HOSTS = /(^|\.)collctiv\.com$|(^|\.)collctiv\.app\.link$/i;

const VAGUE_ANCHORS = [
  'click here',
  'here',
  'read more',
  'more',
  'learn more',
  'find out more',
  'this',
  'link',
];

function classify(href: string): 'internal' | 'external' | 'relative' | 'empty' | 'invalid' {
  if (!href.trim()) return 'empty';
  if (href.startsWith('/')) return 'relative';
  try {
    const u = new URL(href);
    if (!/^https?:$/.test(u.protocol)) return u.protocol === 'mailto:' ? 'external' : 'invalid';
    return INTERNAL_HOSTS.test(u.hostname) ? 'internal' : 'external';
  } catch {
    return 'invalid';
  }
}

type Props = Parameters<CustomFieldRender<LinkValue>>[0] & {
  internalOnly?: boolean;
};

const EMPTY: LinkValue = { href: '', label: '' };

export function LinkField({ value, onChange, readOnly, internalOnly }: Props): React.ReactElement {
  const v: LinkValue = { ...EMPTY, ...(value ?? {}) };
  const set = (patch: Partial<LinkValue>): void => onChange({ ...v, ...patch });

  const kind = classify(v.href);
  const vague = VAGUE_ANCHORS.includes(v.label.trim().toLowerCase().replace(/[.!→]/g, '').trim());

  return (
    <div className="lpf">
      <label className="lpf__row">
        <span className="lpf__label">Link text</span>
        <input
          className={`lpf__input${vague ? ' lpf__input--warn' : ''}`}
          value={v.label}
          disabled={readOnly}
          placeholder="Start a leaving collection"
          onChange={(e) => set({ label: e.target.value })}
        />
        {vague && (
          <span className="lpf__error">
            Vague anchor text. Say where it goes — “Start a leaving collection”, not “click here”.
          </span>
        )}
      </label>

      <label className="lpf__row">
        <span className="lpf__label">URL</span>
        <input
          className="lpf__input"
          value={v.href}
          disabled={readOnly}
          placeholder={internalOnly ? '/blog/best-teacher-gift-ideas' : 'https://…'}
          onChange={(e) => set({ href: e.target.value })}
        />
        {kind === 'invalid' && <span className="lpf__error">That doesn’t look like a URL.</span>}
        {internalOnly && kind === 'external' && (
          <span className="lpf__error">
            This slot is for internal links — they’re what the ≥2 internal-link target counts.
          </span>
        )}
        {kind === 'external' && !internalOnly && (
          <span className="lpf__hint">External — will open in a new tab.</span>
        )}
      </label>
    </div>
  );
}
